import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { DeleteConfirmationModal } from "../components/ui/delete-confirmation-modal";
import AddLotWizard from "../components/AddLotWizard";
import { 
  Building2,
  Search,
  Plus,
  ArrowLeft,
  MapPin,
  Ruler,
  User,
  Trash2,
  Edit,
  MoreVertical,
  Map
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import AdminSidebar from "../components/layout/AdminSidebar";

const PropertiesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);
  
  // Delete state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [relatedRecords, setRelatedRecords] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProperties = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      
      const response = await axios.get(`${API}/properties?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
      
      // Also fetch owners for the wizard
      const ownersRes = await axios.get(`${API}/owners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOwners(ownersRes.data || []);
    } catch (error) {
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, [token, searchQuery]);

  // Delete property handlers
  const handleDeleteClick = async (property) => {
    setSelectedProperty(property);
    try {
      const response = await axios.get(`${API}/properties/${property.property_id}/related-records`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRelatedRecords(response.data);
    } catch (error) {
      console.error("Failed to check related records:", error);
      setRelatedRecords(null);
    }
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProperty) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/properties/${selectedProperty.property_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(relatedRecords?.has_related_records 
        ? "Property archived (soft deleted)" 
        : "Property deleted");
      setDeleteOpen(false);
      setSelectedProperty(null);
      setRelatedRecords(null);
      fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete property");
    } finally {
      setDeleting(false);
    }
  };

  const content = (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Page Title */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-primary" />
        </div>
        <div>
          <span className="font-serif text-lg font-semibold block">Properties</span>
          <span className="text-xs text-muted-foreground">Manage lots and property records</span>
        </div>
      </div>

      {/* Search and Create */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by lot number or address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-properties-input"
          />
        </div>
        
        <Button 
          data-testid="create-property-btn" 
          className="bg-primary text-primary-foreground"
          onClick={() => setWizardOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Lot
        </Button>
      </div>

      {/* Properties Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading properties...</div>
        ) : properties.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No properties found</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setWizardOpen(true)}
                data-testid="add-first-property-btn"
              >
                Add your first property
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {properties.map((property) => (
              <Card key={property.property_id} data-testid={`property-${property.property_id}`} className="hover:shadow-floating transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg">Lot {property.lot_number}</h3>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/board/properties/${property.property_id}`)}>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/board/gis?property=${property.property_id}`)}>
                              <Map className="w-4 h-4 mr-2" /> View on Map
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-destructive focus:text-destructive"
                              onClick={() => handleDeleteClick(property)}
                              data-testid={`delete-property-${property.property_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <MapPin className="w-4 h-4" />
                        {property.address}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t flex items-center gap-4 text-sm">
                    {property.acreage && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Ruler className="w-4 h-4" />
                        {property.acreage} acres
                      </div>
                    )}
                    {property.owner_id && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <User className="w-4 h-4" />
                        Assigned
                      </div>
                    )}
                    {property.gis_lot_boundary && (
                      <Badge variant="outline" className="text-green-600 border-green-200">
                        <Map className="w-3 h-3 mr-1" />
                        GIS
                      </Badge>
                    )}
                  </div>
                  {property.notes && (
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{property.notes}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete Property"
        description="Are you sure you want to delete this property? This action will archive the property record."
        itemName={selectedProperty ? `Lot ${selectedProperty.lot_number} - ${selectedProperty.address}` : ''}
        itemType="Property"
        warningMessage={relatedRecords?.warning_message}
        relatedRecords={relatedRecords}
        onConfirm={handleDeleteConfirm}
        isLoading={deleting}
        deleteButtonText={relatedRecords?.has_related_records ? "Archive Property" : "Delete Property"}
      />

      {/* Add Lot Wizard */}
      <AddLotWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onSuccess={() => {
          fetchProperties();
        }}
        token={token}
        owners={owners}
        API={API}
      />
    </div>
  );

  return <AdminSidebar>{content}</AdminSidebar>;
};

export default PropertiesPage;
